import React, { useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Button,
  Grid,
  Icon,
  Link,
  Typography,
} from '@material-ui/core';
import { Trans, useTranslation } from 'react-i18next';
import { LockIcon } from '../../assets/icons';
import config from '../../config';
import { getCookie } from '../../utils/cookies';
import useStyles from './styles';

const LANGUAGE_COOKIE = 'mhub_profile_language_v1';

// Wording and actions follow the Showroom (mhub-book-web Forbidden) and Loan Tracker / Enterprise
// (mhub-web Forbidden) pages: "Oops! Looks like you do not have access.", a safe way back to the
// MHub landing page and a way to sign in as a different user.
//
// `message` is the reason mhub-api gave for a 403 on /authenticated/ (LMS-5174). Today that is
// only ever the organisation-suspended notice, and the API sends it in English regardless of the
// user's language, so it is shown through the translated key with the API text as the fallback.
const AccessDenied = ({ message }) => {
  const classes = useStyles();
  const { t, i18n } = useTranslation();

  // When rendered from the Auth provider for a suspended organisation there is no authenticated
  // user, so LanguageInitialization never runs. Reuse the language this browser last used.
  useEffect(() => {
    const cookie = getCookie(LANGUAGE_COOKIE);
    if (cookie && cookie !== i18n.language && (i18n.options.supportedLngs || []).includes(cookie)) {
      i18n.changeLanguage(cookie);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const emailSupport = t('component.email-support.', 'support@mhub.my');
  document.title = t('component.label.access_denied', 'Access Denied');
  return (
    <Grid
      container
      direction="column"
      justifyContent="center"
      alignItems="center"
      className={classes.accessDenied}
    >
      <Icon>
        <img alt="lock" src={LockIcon} />
      </Icon>
      <Typography variant="h1">
        {t('access_denied.title', 'Oops!')}
      </Typography>

      <Typography variant="body1" className={classes.subtitle}>
        {t('access_denied.description', 'Looks like you do not have access.')}
        {message && (
          <>
            <br />
            {t('access_denied.suspended', message)}
          </>
        )}
        <br />
        {/* <Trans> turns <supportLink> into a real link; plain t() left the tag as text. */}
        <Trans
          i18nKey="access_denied.assistance"
          values={{ email: emailSupport }}
          components={{ supportLink: <Link href={`mailto:${emailSupport}`} /> }}
        >
          {'Kindly contact your administrator or '}
          <supportLink>{'{email}'}</supportLink>
          {' for assistance.'}
        </Trans>
      </Typography>

      <Grid
        container
        className={classes.buttonGroup}
        justifyContent="center"
      >
        {/* Back to the MHub product selection (secure landing page). */}
        <Button variant="contained" href={config.app.secureUrl}>
          {t('access_denied.return_home', 'Return home safely')}
        </Button>
        <Button variant="outlined" href={`${config.app.secureUrl}/logout`}>
          {t('access_denied.switch_user', 'Login as a different user')}
        </Button>
      </Grid>
    </Grid>
  );
};

AccessDenied.propTypes = {
  message: PropTypes.string,
};

AccessDenied.defaultProps = {
  message: '',
};

export default AccessDenied;
